<script setup lang="ts">
/**
 * The connectivity banner.
 *
 * The single most important string on the screen during an outage, and the
 * reason the plan names three states rather than two: a server who sees a
 * spinner or a generic "connection problem" STOPS TAKING ORDERS, on a till
 * that is working perfectly well. So the banner never says "error" — it says
 * whether they can sell.
 *
 * `role="status"` rather than `role="alert"`: degraded selling is information,
 * not an interruption, and an assertive live region would talk over a screen
 * reader user mid-order. `cannot_sell` escalates to assertive, because that
 * one genuinely stops the work.
 */
import { computed } from "vue";
import type { DegradationReason, SellState } from "@gf/sync-client";
import type { Translator } from "@gf/kernel/i18n";

const props = defineProps<{
  state: SellState;
  reasons: readonly DegradationReason[];
  queuedCount: number;
  t: Translator;
  /** The training venue: never quiet, and it says so first. */
  training?: boolean;
}>();

const KEYS: Record<SellState, string> = {
  online: "pos.status.online",
  degraded_selling: "pos.status.degraded",
  cannot_sell: "pos.status.cannotSell",
};

const headline = computed(() => props.t.t(props.training ? "pos.training" : KEYS[props.state]));

/** Why, in words a manager can act on — never a code. */
const detail = computed(() =>
  props.reasons.map((reason) => props.t.t(`pos.reason.${reason}`)).join(" · "),
);

const queued = computed(() =>
  props.queuedCount > 0 ? props.t.t("pos.status.queued", { count: props.queuedCount }) : "",
);

const live = computed(() => (props.state === "cannot_sell" ? "assertive" : "polite"));
</script>

<template>
  <!--
    THE HEALTHY STATE IS THE QUIET ONE.

    This was one full-width bar at every state, so a till that was working
    perfectly spent the whole shift with a grey strip across the top of it
    saying so. Forty pixels of a screen somebody is standing at, permanently
    given to the absence of news — and, worse, a banner that looks the same at
    rest as in trouble is a banner people stop reading.

    So `online` collapses to a dot and a word, and degradation EXPANDS. The
    state is still in the text at every level, which SC 1.4.1 requires and
    which a colourblind server on a red-versus-amber bar depends on.
  -->
  <div
    class="banner"
    :class="[`banner--${training && state === 'online' ? 'degraded_selling' : state}`, { 'banner--quiet': state === 'online' && !training }]"
    :data-state="state"
    :data-testid="training ? 'training' : undefined"
    role="status"
    :aria-live="live"
  >
    <span class="banner__dot" aria-hidden="true" />
    <strong class="banner__headline">{{ headline }}</strong>
    <span v-if="detail" class="banner__detail">{{ detail }}</span>
    <span v-if="queued" class="banner__queued">{{ queued }}</span>
  </div>
</template>

<style scoped>
.banner {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gf-space-3);
  align-items: baseline;
  padding: var(--gf-space-2) var(--gf-space-4);
  font-size: var(--gf-text-base);
  /* Colour is never the only signal: the headline text states the condition
     outright, which is what SC 1.4.1 requires and what a colourblind server
     needs at a glance. The hairline under it belongs to the till's top strip
     (`.pos__top`), which it shares with the language switch. */
  transition:
    background-color var(--gf-motion-base) var(--gf-ease-out),
    color var(--gf-motion-base) var(--gf-ease-out),
    padding var(--gf-motion-base) var(--gf-ease-out);
}

/*
  At rest: a line of small muted text with a green dot, about the height of the
  text itself. It is still readable, still announced, and no longer competing
  with the check for the server's attention.
*/
.banner--quiet {
  align-items: center;
  gap: var(--gf-space-2);
  padding: var(--gf-space-1) var(--gf-space-4);
  font-size: var(--gf-text-xs);
  background: var(--gf-color-canvas);
  color: var(--gf-color-text-muted);
}

.banner__dot {
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: var(--gf-radius-full);
  background: currentColor;
}

/* Only the quiet state needs the dot to carry a colour of its own — the other
   two are a full field of it. */
.banner--quiet .banner__dot {
  background: var(--gf-color-success);
}

/* Degradation gets the weight back, and then some. */
.banner--degraded_selling {
  background: var(--gf-color-warning);
  color: var(--gf-color-on-warning);
  font-weight: var(--gf-weight-medium);
}

.banner--cannot_sell {
  background: var(--gf-color-danger);
  color: var(--gf-color-on-danger);
  font-weight: var(--gf-weight-medium);
}

.banner__headline {
  font-weight: var(--gf-weight-semibold);
}

.banner--quiet .banner__headline {
  font-weight: var(--gf-weight-medium);
}

.banner__detail,
.banner__queued {
  font-size: var(--gf-text-sm);
}

.banner--quiet .banner__detail,
.banner--quiet .banner__queued {
  font-size: inherit;
}
</style>
